import type {
  AdminUsageDashboardResponse,
  AdminUsageDepartmentBreakdown,
  AdminUsageFilterOption,
  AdminUsageModelBreakdown,
  AdminUsageSeatRow,
  AdminUsageTrendPoint,
  UsageByKeyEntry,
  UsageRequestLogEntry,
} from '@nadoutong/contracts';
import {
  Building2,
  Check,
  ChevronDown,
  Download,
  Landmark,
  LogIn,
  LogOut,
  RefreshCw,
  Save,
  Search,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { type FocusEvent, useMemo, useState } from 'react';
import { PlatformApiError } from '../api/client.ts';
import { getAdminUsageDashboardCsv, type AdminUsageDashboardClientQuery } from '../api/usage.ts';
import { useDisplayRate } from '../api/useBilling.ts';
import { useCatalog } from '../api/useCatalog.ts';
import {
  useAdminUsageDashboard,
  useUsageByKey,
  useUsageDaily,
  useUsageRequests,
  useUsageSummary,
} from '../api/useUsage.ts';
import { DailyUsageChart } from '../components/DailyUsageChart.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import {
  formatTokens,
  formatUsdCentsAsCny,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  parseTimestampMs,
  unitAwareTokenCell,
} from '../components/formatters.ts';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';

type RangeOption = {
  readonly id: string;
  readonly label: string;
  readonly days: number;
};

type TokenMetric = 'total' | 'in' | 'out';

type MemberCacheSummary = {
  readonly successRequests: number;
  readonly cacheHitRequests: number;
  readonly cacheEligibleInputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
  readonly requestCacheHitRate: number | null;
  readonly tokenCacheHitRate: number | null;
  readonly cacheReadWriteRatio: number | null;
};

const RANGE_OPTIONS: readonly RangeOption[] = [
  { id: '7d', label: '7D', days: 7 },
  { id: '30d', label: '30D', days: 30 },
  { id: '90d', label: '90D', days: 90 },
];

const MEMBER_RANGE_OPTIONS: readonly RangeOption[] = [
  { id: '24h', label: '24h', days: 1 },
  { id: '7d', label: '7d', days: 7 },
  { id: '30d', label: '30d', days: 30 },
];

const COMPACT_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});

interface ModelSelection {
  /** Composite option id (`vendor:model`); empty means "all models". */
  readonly id: string;
  readonly model: string;
  readonly vendor: string;
}

const NO_MODEL_SELECTION: ModelSelection = { id: '', model: '', vendor: '' };

export function Usage(): JSX.Element {
  const { user } = useSession();
  const canReadOrgUsage = hasPermission(user, 'org:usage:read');
  return canReadOrgUsage ? <AdminUsageDashboard /> : <MemberUsageDashboard />;
}

function AdminUsageDashboard(): JSX.Element {
  const { fetchImpl } = useSession();
  const displayRateQ = useDisplayRate();
  const [range, setRange] = useState<RangeOption>(
    RANGE_OPTIONS[1] ?? RANGE_OPTIONS[0] ?? { id: '30d', label: '30D', days: 30 },
  );
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('day');
  const [groupBy, setGroupBy] = useState<'model' | 'department' | 'member'>('model');
  // #1672: a model dimension is keyed by vendor + model, so the selection
  // carries the option's structured fields alongside its composite id.
  const [modelSelection, setModelSelection] = useState<ModelSelection>(NO_MODEL_SELECTION);
  const [apiKeyId, setApiKeyId] = useState('');
  const [department, setDepartment] = useState('');
  const [memberAccountId, setMemberAccountId] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [metric, setMetric] = useState<TokenMetric>('total');
  const [exportState, setExportState] = useState<'idle' | 'working' | 'done' | 'error'>('idle');

  const queryRange = useMemo(() => rangeFromDays(range.days), [range.days]);
  const query = useMemo<AdminUsageDashboardClientQuery>(
    () => ({
      ...queryRange,
      granularity,
      groupBy,
      model: modelSelection.model.length > 0 ? modelSelection.model : undefined,
      // #1672: `vendor` only narrows an already-selected model name.
      vendor:
        modelSelection.model.length > 0 && modelSelection.vendor.length > 0
          ? modelSelection.vendor
          : undefined,
      apiKeyId: apiKeyId.length > 0 ? apiKeyId : undefined,
      department: department.length > 0 ? department : undefined,
      memberAccountId: memberAccountId.length > 0 ? memberAccountId : undefined,
      q: search.length > 0 ? search : undefined,
      limit: 12,
      offset,
    }),
    [
      apiKeyId,
      department,
      granularity,
      groupBy,
      memberAccountId,
      modelSelection,
      offset,
      queryRange,
      search,
    ],
  );
  const dashboardQ = useAdminUsageDashboard(query, true);
  const dashboard = dashboardQ.data;
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  async function handleExport(): Promise<void> {
    setExportState('working');
    try {
      const csv = await getAdminUsageDashboardCsv(fetchImpl, query);
      triggerCsvDownload(csv, `usage-dashboard-${range.id}.csv`);
      setExportState('done');
    } catch {
      setExportState('error');
    }
  }

  return (
    <section data-testid="usage-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex max-w-3xl flex-col gap-2">
          <span className="inline-flex w-fit items-center gap-2 font-mono text-caption font-medium uppercase text-ink-low">
            <span aria-hidden="true" className="h-2 w-2 rounded-sm bg-rust" />
            Token admin · {range.days}-day window
          </span>
          <h2 className="font-display text-display text-ink">Token usage</h2>
          <p className="text-body leading-6 text-ink-mid">
            Aggregate model consumption across every member, key, and route.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            void handleExport();
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-line bg-card px-4 py-2 text-body font-medium text-ink transition-colors hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
          data-testid="usage-export-csv"
        >
          <Download aria-hidden="true" className="h-4 w-4" />
          <span>{exportState === 'working' ? 'Exporting...' : 'Export CSV'}</span>
        </button>
      </header>

      <AdminFilterBar
        dashboard={dashboard}
        range={range}
        granularity={granularity}
        groupBy={groupBy}
        model={modelSelection.id}
        apiKeyId={apiKeyId}
        department={department}
        memberAccountId={memberAccountId}
        onRange={(next) => {
          setRange(next);
          setOffset(0);
        }}
        onGranularity={(next) => {
          setGranularity(next);
          setOffset(0);
        }}
        onGroupBy={(next) => {
          setGroupBy(next);
          setOffset(0);
        }}
        onModel={(next) => {
          const option = (dashboard?.filters.models ?? []).find((entry) => entry.id === next);
          setModelSelection(
            option === undefined
              ? NO_MODEL_SELECTION
              : { id: option.id, model: option.model, vendor: option.vendor },
          );
          setOffset(0);
        }}
        onApiKey={(next) => {
          setApiKeyId(next);
          setOffset(0);
        }}
        onDepartment={(next) => {
          setDepartment(next);
          setOffset(0);
        }}
        onMember={(next) => {
          setMemberAccountId(next);
          setOffset(0);
        }}
      />

      {dashboardQ.isLoading ? (
        <AdminDashboardSkeleton />
      ) : dashboardQ.isError ? (
        <UsageError error={dashboardQ.error} testId="usage-admin-error" />
      ) : dashboard !== undefined ? (
        <>
          <div
            className="flex flex-col gap-2 rounded-lg border border-line bg-raised px-4 py-3 text-body text-ink-low sm:flex-row sm:items-center sm:justify-between"
            data-testid="usage-window-bar"
          >
            <span>
              Workspace <span className="font-medium text-ink">All workspaces</span>
            </span>
            <span className="font-mono">
              {formatIsoDate(dashboard.window.from)} - {formatIsoDate(dashboard.window.to)} · refreshed{' '}
              {formatTime(dashboard.refreshedAt)}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <AdminSummaryCard
              label="Tokens in"
              value={`${formatCompactCount(dashboard.summary.totalInputTokens)} tokens`}
              detail={`${formatTokens(dashboard.summary.totalRequests)} requests`}
              icon={LogIn}
              testId="usage-summary-input-tokens"
            />
            <AdminSummaryCard
              label="Tokens out"
              value={`${formatCompactCount(dashboard.summary.totalOutputTokens)} tokens`}
              detail={`${formatTokens(dashboard.summary.successRequests)} succeeded`}
              icon={LogOut}
              testId="usage-summary-output-tokens"
            />
            <AdminSummaryCard
              label="Cache write"
              value={`${formatCompactCount(dashboard.summary.totalCacheWriteTokens)} tokens`}
              detail="prompt cache creation"
              icon={Save}
              testId="usage-summary-cache-write-tokens"
            />
            <AdminSummaryCard
              label="Cache read"
              value={`${formatCompactCount(dashboard.summary.totalCacheReadTokens)} tokens`}
              detail="prompt cache reuse"
              icon={RefreshCw}
              testId="usage-summary-cache-read-tokens"
            />
            <AdminSummaryCard
              label="Blended spend"
              value={formatUsdMicrosOrCentsAsCnyDisplay(
                dashboard.summary.totalCostUsdMicros,
                dashboard.summary.totalCostUsdCents,
                cnyPerUsd,
              )}
              detail="usage_logs cost metadata"
              icon={Landmark}
              testId="usage-summary-cost"
            />
            <AdminSummaryCard
              // #1667: window-scoped usage and roster population are different
              // calibers; showing them as one ratio invited a false
              // part-of-whole reading.
              label="Seats with usage"
              value={formatTokens(dashboard.summary.seatsWithUsage)}
              detail={`${formatTokens(dashboard.summary.totalSeats)} seats on roster · ${formatTokens(dashboard.summary.activeApiKeys)} active keys`}
              icon={Users}
              testId="usage-summary-seats-with-usage"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
            <article
              data-testid="usage-admin-trend-card"
              className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
            >
              <PanelHeader
                title="Token usage trend"
                subtitle={`Stacked by ${dashboard.window.groupBy} · ${formatIsoDate(dashboard.window.from)} - ${formatIsoDate(dashboard.window.to)}`}
                action={<MetricToggle metric={metric} onChange={setMetric} />}
              />
              <StackedTrendChart points={dashboard.trend} metric={metric} />
              <div className="flex items-center justify-between rounded-lg border border-line bg-raised px-3 py-2 text-caption text-ink-low">
                <span>Hover any bar to inspect a single window</span>
                <span className="font-mono text-ink">
                  total · {formatCompactCount(dashboard.summary.totalInputTokens + dashboard.summary.totalOutputTokens)} tokens
                </span>
              </div>
            </article>

            <ModelMixPanel models={dashboard.models} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <DepartmentMixPanel departments={dashboard.departments} />
            <RoutingHealthPanel dashboard={dashboard} />
          </div>

          <SeatUsageTable
            dashboard={dashboard}
            cnyPerUsd={cnyPerUsd}
            search={search}
            onSearch={(next) => {
              setSearch(next);
              setOffset(0);
            }}
            onOffset={setOffset}
          />
        </>
      ) : null}
      {exportState === 'error' ? (
        <p className="text-body text-bad" role="alert" data-testid="usage-export-error">
          CSV export failed.
        </p>
      ) : null}
    </section>
  );
}

function MemberUsageDashboard(): JSX.Element {
  const t = useT();
  const [range, setRange] = useState<RangeOption>(
    MEMBER_RANGE_OPTIONS[0] ?? { id: '24h', label: '24h', days: 1 },
  );
  const queryRange = useMemo(() => rangeFromDays(range.days), [range.days]);
  const summaryQ = useUsageSummary(queryRange);
  const dailyQ = useUsageDaily(range.days);
  const byKeyQ = useUsageByKey(queryRange);
  const requestsQ = useUsageRequests({ ...queryRange, limit: 50, offset: 0 });
  const displayRateQ = useDisplayRate();
  const catalogQ = useCatalog();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const summary = summaryQ.data;
  const cacheSummary = useMemo(
    () => summarizeMemberCache(byKeyQ.data?.keys ?? []),
    [byKeyQ.data?.keys],
  );
  const brandedSkuNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const sku of catalogQ.data?.skus ?? []) map.set(sku.sku_id, sku.sku_name);
    return map;
  }, [catalogQ.data?.skus]);

  return (
    <section data-testid="usage-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex max-w-3xl flex-col gap-2">
          <span className="inline-flex w-fit items-center gap-2 rounded-lg border border-line bg-raised px-3 py-1 text-caption font-medium text-ink-low">
            <span aria-hidden="true" className="h-2 w-2 rounded-sm bg-rust" />
            {t('usage.eyebrow')}
          </span>
          <h2 className="font-display text-display text-ink">My token usage</h2>
          <p className="text-body leading-6 text-ink-low">
            Your own usage, budget metadata, API keys, and request billing logs.
          </p>
        </div>
        <RangePicker options={MEMBER_RANGE_OPTIONS} value={range} onChange={setRange} />
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <AdminSummaryCard
          label="Tokens in"
          value={summary !== undefined ? `${formatCompactCount(summary.totalInputTokens)} tokens` : '-'}
          detail={`${range.label} personal scope`}
          icon={LogIn}
          testId="usage-summary-input-tokens"
        />
        <AdminSummaryCard
          label="Tokens out"
          value={summary !== undefined ? `${formatCompactCount(summary.totalOutputTokens)} tokens` : '-'}
          detail={summary !== undefined ? `${formatTokens(summary.totalRequests)} requests` : 'Loading'}
          icon={LogOut}
          testId="usage-summary-output-tokens"
        />
        <AdminSummaryCard
          label="Cache write"
          value={summary !== undefined ? `${formatCompactCount(summary.totalCacheWriteTokens)} tokens` : '-'}
          detail={`${range.label} cache creation`}
          icon={Save}
          testId="usage-summary-cache-write-tokens"
        />
        <AdminSummaryCard
          label="Cache read"
          value={summary !== undefined ? `${formatCompactCount(summary.totalCacheReadTokens)} tokens` : '-'}
          detail={`${range.label} cache reuse`}
          icon={RefreshCw}
          testId="usage-summary-cache-read-tokens"
        />
        <AdminSummaryCard
          label="Spend"
          value={
            summary !== undefined
              ? formatUsdMicrosOrCentsAsCnyDisplay(
                  summary.totalCostUsdMicros,
                  summary.totalCostUsdCents,
                  cnyPerUsd,
                )
              : '-'
          }
          detail="shown from billing metadata"
          icon={Landmark}
          testId="usage-summary-cost"
        />
        <AdminSummaryCard
          label="Cache hit"
          value={formatNullablePercent(cacheSummary.requestCacheHitRate)}
          detail={`${formatNullablePercent(cacheSummary.tokenCacheHitRate)} token hit · ${formatTokens(cacheSummary.successRequests)} ok`}
          icon={RefreshCw}
          testId="usage-summary-member-scope"
        />
      </div>

      <article className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5">
        <PanelHeader title="Personal token trend" subtitle="Input and output tokens from your own keys." />
        {dailyQ.isLoading ? (
          <Skeleton className="h-64" />
        ) : dailyQ.isError ? (
          <UsageError error={dailyQ.error} testId="usage-member-daily-error" />
        ) : (
          <DailyUsageChart buckets={dailyQ.data?.days ?? []} testId="usage-daily-chart" />
        )}
      </article>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <MemberKeyPanel
          keys={byKeyQ.data?.keys ?? []}
          isLoading={byKeyQ.isLoading}
          cnyPerUsd={cnyPerUsd}
          brandedSkuNameById={brandedSkuNameById}
        />
        <MemberRequestPanel rows={requestsQ.data?.rows ?? []} isLoading={requestsQ.isLoading} />
      </div>
    </section>
  );
}

function AdminFilterBar(props: {
  readonly dashboard: AdminUsageDashboardResponse | undefined;
  readonly range: RangeOption;
  readonly granularity: 'day' | 'week' | 'month';
  readonly groupBy: 'model' | 'department' | 'member';
  readonly model: string;
  readonly apiKeyId: string;
  readonly department: string;
  readonly memberAccountId: string;
  readonly onRange: (range: RangeOption) => void;
  readonly onGranularity: (value: 'day' | 'week' | 'month') => void;
  readonly onGroupBy: (value: 'model' | 'department' | 'member') => void;
  readonly onModel: (value: string) => void;
  readonly onApiKey: (value: string) => void;
  readonly onDepartment: (value: string) => void;
  readonly onMember: (value: string) => void;
}): JSX.Element {
  return (
    <div
      data-testid="usage-admin-filter-bar"
      className="flex flex-col gap-3 rounded-lg border border-line bg-card p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip icon={Building2} label="Workspace" value="All workspaces" />
        <RangePicker options={RANGE_OPTIONS} value={props.range} onChange={props.onRange} />
        <SelectControl
          label="Model"
          value={props.model}
          onChange={props.onModel}
          allLabel="All models"
          options={props.dashboard?.filters.models ?? []}
          testId="usage-filter-model"
        />
        <ApiKeySelectControl
          label="API key"
          value={props.apiKeyId}
          onChange={props.onApiKey}
          allLabel="All keys"
          showRevokedLabel="Show revoked keys"
          activeLabel="Active"
          revokedLabel="Revoked"
          emptyLabel="No active API keys"
          options={props.dashboard?.filters.apiKeys ?? []}
          testId="usage-filter-api-key"
        />
        <SelectControl
          label="Org unit"
          value={props.department}
          onChange={props.onDepartment}
          allLabel="All org units"
          options={props.dashboard?.filters.departments ?? []}
          testId="usage-filter-department"
        />
        <SelectControl
          label="Member"
          value={props.memberAccountId}
          onChange={props.onMember}
          allLabel="All members"
          options={props.dashboard?.filters.members ?? []}
          testId="usage-filter-member"
        />
        <select
          value={props.groupBy}
          onChange={(event) => props.onGroupBy(event.target.value as 'model' | 'department' | 'member')}
          className="h-10 rounded-lg border border-line bg-raised px-3 text-body font-medium text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
          data-testid="usage-filter-group-by"
        >
          <option value="model">Group by model</option>
          <option value="department">Group by org unit</option>
          <option value="member">Group by member</option>
        </select>
      </div>
      <div className="inline-flex w-fit items-center gap-1 rounded-lg bg-raised p-1">
        {(['day', 'week', 'month'] as const).map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => props.onGranularity(item)}
            className={[
              'rounded-md px-3 py-1.5 text-caption font-medium uppercase transition-colors',
              props.granularity === item
                ? 'bg-card text-forest'
                : 'text-ink-low hover:text-ink',
            ].join(' ')}
            data-testid={`usage-granularity-${item}`}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
}

function RangePicker(props: {
  readonly options: readonly RangeOption[];
  readonly value: RangeOption;
  readonly onChange: (range: RangeOption) => void;
}): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label="Time range"
      data-testid="usage-range-picker"
      className="inline-flex w-fit items-center gap-1 rounded-lg border border-line bg-raised p-1"
    >
      {props.options.map((option) => {
        const active = option.id === props.value.id;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => props.onChange(option)}
            className={[
              'inline-flex min-w-12 items-center justify-center rounded-md px-3 py-1.5 text-caption font-medium transition-colors',
              active ? 'bg-forest text-on-forest' : 'bg-transparent text-ink-low hover:text-ink',
            ].join(' ')}
            data-testid={`usage-range-${option.id}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function FilterChip(props: { readonly icon: LucideIcon; readonly label: string; readonly value: string }): JSX.Element {
  const IconGlyph = props.icon;
  return (
    <span className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-raised px-3 text-body text-ink-low">
      <IconGlyph aria-hidden="true" className="h-4 w-4" />
      <span>{props.label}</span>
      <span className="font-medium text-ink">{props.value}</span>
    </span>
  );
}

function SelectControl(props: {
  readonly label: string;
  readonly value: string;
  readonly allLabel: string;
  readonly options: readonly { id: string; label: string }[];
  readonly testId: string;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <label className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-raised px-3 text-body text-ink-low">
      <span>{props.label}</span>
      <select
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className="min-w-32 bg-transparent font-medium text-ink focus-visible:outline-none"
        data-testid={props.testId}
      >
        <option value="">{props.allLabel}</option>
        {props.options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ApiKeySelectControl(props: {
  readonly label: string;
  readonly value: string;
  readonly allLabel: string;
  readonly showRevokedLabel: string;
  readonly activeLabel: string;
  readonly revokedLabel: string;
  readonly emptyLabel: string;
  readonly options: readonly AdminUsageFilterOption[];
  readonly testId: string;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const selectedOption = props.options.find((option) => option.id === props.value);
  const selectedRevoked = selectedOption !== undefined && isRevokedApiKeyOption(selectedOption);
  const visibleOptions = props.options.filter((option) => showRevoked || !isRevokedApiKeyOption(option));

  function handleBlur(event: FocusEvent<HTMLDivElement>): void {
    const nextFocus = event.relatedTarget;
    if (nextFocus !== null && event.currentTarget.contains(nextFocus as Node)) return;
    setOpen(false);
  }

  function setShowRevokedKeys(next: boolean): void {
    setShowRevoked(next);
    if (!next && selectedRevoked) {
      props.onChange('');
    }
  }

  function selectValue(next: string): void {
    props.onChange(next);
    setOpen(false);
  }

  return (
    <div className="relative inline-flex" onBlur={handleBlur}>
      <button
        type="button"
        className="inline-flex h-10 min-w-72 items-center gap-2 rounded-lg border border-line bg-raised px-3 text-body text-ink-low transition-colors hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        data-testid={props.testId}
      >
        <span>{props.label}</span>
        <span className="min-w-0 flex-1 truncate text-left font-medium text-ink">
          {selectedOption?.label ?? props.allLabel}
        </span>
        {selectedOption !== undefined ? (
          <ApiKeyStatusBadge
            label={selectedRevoked ? props.revokedLabel : props.activeLabel}
            revoked={selectedRevoked}
          />
        ) : null}
        <ChevronDown aria-hidden="true" className="h-4 w-4 text-ink-low" />
      </button>
      {open ? (
        <div
          className="absolute left-0 top-11 z-30 w-80 overflow-hidden rounded-lg border border-line bg-card"
          data-testid={`${props.testId}-menu`}
        >
          <label className="flex cursor-pointer items-center gap-2 border-b border-line px-3 py-2 text-caption font-medium text-ink-mid hover:bg-active">
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(event) => setShowRevokedKeys(event.currentTarget.checked)}
              className="h-4 w-4 accent-forest"
              data-testid={`${props.testId}-show-revoked`}
            />
            <span>{props.showRevokedLabel}</span>
          </label>
          <div role="listbox" className="max-h-72 overflow-y-auto p-1" aria-label={props.label}>
            <ApiKeyOptionButton
              value=""
              label={props.allLabel}
              selected={props.value.length === 0}
              onSelect={selectValue}
              testId={`${props.testId}-option-all`}
            />
            {visibleOptions.length === 0 ? (
              <p className="px-3 py-3 text-caption text-ink-low" data-testid={`${props.testId}-empty`}>
                {props.emptyLabel}
              </p>
            ) : (
              visibleOptions.map((option) => {
                const revoked = isRevokedApiKeyOption(option);
                return (
                  <ApiKeyOptionButton
                    key={option.id}
                    value={option.id}
                    label={option.label}
                    selected={props.value === option.id}
                    onSelect={selectValue}
                    statusLabel={revoked ? props.revokedLabel : props.activeLabel}
                    revoked={revoked}
                    testId={`${props.testId}-option-${testIdSlug(option.id)}`}
                  />
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ApiKeyOptionButton(props: {
  readonly value: string;
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: (value: string) => void;
  readonly testId: string;
  readonly statusLabel?: string;
  readonly revoked?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="option"
      aria-selected={props.selected}
      onClick={() => props.onSelect(props.value)}
      className={[
        'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-body transition-colors',
        props.selected
          ? 'bg-forest text-on-forest'
          : 'text-ink hover:bg-active',
      ].join(' ')}
      data-testid={props.testId}
    >
      <span className="inline-flex w-5 justify-center" aria-hidden="true">
        {props.selected ? <Check className="h-4 w-4 text-on-forest" /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate font-medium">{props.label}</span>
      {props.statusLabel !== undefined ? (
        <ApiKeyStatusBadge label={props.statusLabel} revoked={props.revoked === true} selected={props.selected} />
      ) : null}
    </button>
  );
}

function ApiKeyStatusBadge(props: {
  readonly label: string;
  readonly revoked: boolean;
  readonly selected?: boolean;
}): JSX.Element {
  const selected = props.selected === true;
  return (
    <span
      className={[
        'shrink-0 rounded-md px-1.5 py-0.5 text-mono-label font-medium',
        selected
          ? 'bg-on-forest/20 text-on-forest'
          : props.revoked
            ? 'bg-active text-bad'
            : 'bg-active text-forest',
      ].join(' ')}
    >
      {props.label}
    </span>
  );
}

function isRevokedApiKeyOption(option: AdminUsageFilterOption): boolean {
  return typeof option.revokedAt === 'string' && option.revokedAt.length > 0;
}

function AdminSummaryCard(props: {
  readonly label: string;
  readonly value: string;
  readonly detail: string;
  readonly icon: LucideIcon;
  readonly testId: string;
}): JSX.Element {
  const IconGlyph = props.icon;
  return (
    <article
      data-testid={props.testId}
      className="flex min-h-32 flex-col justify-between gap-4 rounded-lg border border-line bg-card p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-caption font-medium text-ink-mid">{props.label}</span>
        <span className="rounded-md border border-line-soft bg-raised p-1.5 text-ink-low" aria-hidden="true">
          <IconGlyph className="h-4 w-4" />
        </span>
      </div>
      <span className="font-mono text-stat text-ink">{props.value}</span>
      <span className="text-caption text-ink-low">{props.detail}</span>
    </article>
  );
}

function PanelHeader(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly action?: JSX.Element;
}): JSX.Element {
  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex flex-col gap-1">
        <h3 className="text-heading text-ink">{props.title}</h3>
        {props.subtitle !== undefined ? <p className="text-caption text-ink-low">{props.subtitle}</p> : null}
      </div>
      {props.action !== undefined ? <div className="shrink-0">{props.action}</div> : null}
    </header>
  );
}

function MetricToggle(props: {
  readonly metric: TokenMetric;
  readonly onChange: (metric: TokenMetric) => void;
}): JSX.Element {
  return (
    <div className="inline-flex items-center gap-1 rounded-lg bg-raised p-1">
      {(['total', 'in', 'out'] as const).map((item) => (
        <button
          key={item}
          type="button"
          onClick={() => props.onChange(item)}
          className={[
            'rounded-md px-3 py-1.5 text-caption font-medium uppercase transition-colors',
            props.metric === item ? 'bg-card text-forest' : 'text-ink-low hover:text-ink',
          ].join(' ')}
          data-testid={`usage-trend-metric-${item}`}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

function StackedTrendChart(props: {
  readonly points: readonly AdminUsageTrendPoint[];
  readonly metric: TokenMetric;
}): JSX.Element {
  const buckets = [...new Set(props.points.map((point) => point.bucket))];
  const groups = topTrendGroups(props.points, props.metric, 6);
  const maxBucket = Math.max(
    1,
    ...buckets.map((bucket) =>
      props.points
        .filter((point) => point.bucket === bucket && groups.includes(point.groupKey))
        .reduce((sum, point) => sum + trendValue(point, props.metric), 0),
    ),
  );
  if (buckets.length === 0) {
    return (
      <div
        data-testid="usage-admin-trend-empty"
        className="flex h-72 items-center justify-center rounded-lg bg-raised text-body text-ink-low"
      >
        No usage in this window.
      </div>
    );
  }

  const chartWidth = 960;
  const chartHeight = 300;
  const padding = 28;
  const gap = 6;
  const drawableHeight = chartHeight - padding * 2;
  const barWidth = Math.max(8, (chartWidth - padding * 2 - gap * (buckets.length - 1)) / buckets.length);

  return (
    <div className="flex flex-col gap-3">
      <svg
        role="img"
        aria-label="Token usage trend"
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        preserveAspectRatio="none"
        className="block h-72 w-full rounded-lg bg-raised"
        data-testid="usage-admin-trend-chart"
      >
        {buckets.map((bucket, index) => {
          const bucketPoints = props.points.filter(
            (point) => point.bucket === bucket && groups.includes(point.groupKey),
          );
          let yCursor = padding + drawableHeight;
          return (
            <g key={bucket} data-testid={`usage-admin-trend-bar-${index}`}>
              <title>{bucket}</title>
              {bucketPoints.map((point) => {
                const value = trendValue(point, props.metric);
                const height = Math.round((value / maxBucket) * drawableHeight);
                yCursor -= height;
                return height > 0 ? (
                  <rect
                    key={`${bucket}-${point.groupKey}`}
                    x={padding + index * (barWidth + gap)}
                    y={yCursor}
                    width={barWidth}
                    height={height}
                    rx={1}
                    className={trendColorClass(groups.indexOf(point.groupKey))}
                  />
                ) : null;
              })}
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap items-center gap-3 text-caption text-ink-low">
        {groups.map((group, index) => {
          const label = props.points.find((point) => point.groupKey === group)?.groupLabel ?? group;
          return (
            <span key={group} className="inline-flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-sm ${legendColorClass(index)}`} aria-hidden="true" />
              <span className="font-mono">{label}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ModelMixPanel({ models }: { readonly models: readonly AdminUsageModelBreakdown[] }): JSX.Element {
  return (
    <article
      data-testid="usage-model-mix-card"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader title="By model" subtitle="Share of total tokens in the selected window." />
      {models.length === 0 ? (
        <p className="text-body text-ink-low" data-testid="usage-model-mix-empty">
          No model usage in this window.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex h-2 overflow-hidden rounded-sm bg-active">
            {models.slice(0, 6).map((model, index) => (
              <div
                key={`${model.vendor}:${model.model}`}
                className={legendColorClass(index)}
                style={{ width: `${Math.max(2, model.share * 100)}%` }}
              />
            ))}
          </div>
          {models.slice(0, 6).map((model, index) => (
            <div
              key={`${model.vendor}:${model.model}`}
              className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 border-b border-line pb-3 last:border-b-0"
              data-testid={`usage-model-row-${testIdSlug(model.model)}`}
            >
              <span className={`mt-1 h-2.5 w-2.5 rounded-sm ${legendColorClass(index)}`} aria-hidden="true" />
              <div className="min-w-0">
                <p className="truncate font-mono text-body font-medium text-ink">{model.model}</p>
                <p className="font-mono text-caption text-ink-low">
                  in {formatCompactCount(model.inputTokens)} · out {formatCompactCount(model.outputTokens)}
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-body font-medium text-ink">{formatCompactCount(model.totalTokens)}</p>
                <p className="font-mono text-caption text-ink-low">{formatPercent(model.share)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function DepartmentMixPanel({
  departments,
}: {
  readonly departments: readonly AdminUsageDepartmentBreakdown[];
}): JSX.Element {
  const max = Math.max(1, ...departments.map((department) => department.totalTokens));
  return (
    <article
      data-testid="usage-department-mix-card"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader title="Org unit mix" subtitle="Tokens consumed per org unit this period." />
      {departments.length === 0 ? (
        <p className="text-body text-ink-low">No org unit usage in this window.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {departments.slice(0, 8).map((department, index) => (
            <div key={department.department ?? 'unassigned'} className="grid grid-cols-[9rem_minmax(0,1fr)_5rem] items-center gap-3 text-body">
              <span className="truncate text-ink">{department.department ?? 'Unassigned'}</span>
              <div className="h-2 overflow-hidden rounded-sm bg-active">
                <div
                  className={legendColorClass(index)}
                  style={{ width: `${Math.max(2, (department.totalTokens / max) * 100)}%` }}
                />
              </div>
              <span className="text-right font-mono text-ink">{formatCompactCount(department.totalTokens)}</span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function RoutingHealthPanel({ dashboard }: { readonly dashboard: AdminUsageDashboardResponse }): JSX.Element {
  const health = dashboard.routingHealth;
  return (
    <article
      data-testid="usage-routing-health-card"
      className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
    >
      <PanelHeader
        title="Routing health"
        subtitle="Quality-of-service from request billing metadata."
        action={<span className="rounded-md bg-raised px-2 py-1 text-caption font-medium text-forest">All green</span>}
      />
      <dl className="grid gap-0 overflow-hidden rounded-lg border border-line">
        <MetricRow label="Routes evaluated" value={formatTokens(health.routesEvaluated)} />
        <MetricRow label="Cache hit rate" value={formatNullablePercent(health.cacheHitRate)} tone="good" />
        <MetricRow label="Avg latency" value={formatLatency(health.averageLatencyMs)} />
        <MetricRow
          label="Rate-limited"
          value={formatNullablePercent(health.rateLimitedRate)}
          tone={(health.rateLimitedRate ?? 0) > 0 ? 'bad' : 'default'}
        />
        <MetricRow label="Failover events" value={health.failoverEvents === null ? '--' : formatTokens(health.failoverEvents)} />
      </dl>
    </article>
  );
}

function MetricRow(props: {
  readonly label: string;
  readonly value: string;
  readonly tone?: 'default' | 'good' | 'bad';
}): JSX.Element {
  const valueClass =
    props.tone === 'good'
      ? 'text-forest'
      : props.tone === 'bad'
        ? 'text-bad'
        : 'text-ink';
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-line px-3 py-2.5 last:border-b-0">
      <dt className="text-body text-ink-mid">{props.label}</dt>
      <dd className={`font-mono text-body font-medium ${valueClass}`}>{props.value}</dd>
    </div>
  );
}

function SeatUsageTable(props: {
  readonly dashboard: AdminUsageDashboardResponse;
  readonly cnyPerUsd: number | null;
  readonly search: string;
  readonly onSearch: (value: string) => void;
  readonly onOffset: (offset: number) => void;
}): JSX.Element {
  const seats = props.dashboard.seats;
  return (
    <article
      data-testid="usage-by-seat-card"
      className="flex flex-col rounded-lg border border-line bg-card"
    >
      <header className="flex flex-col gap-3 border-b border-line p-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-heading text-ink">Usage by seat</h3>
          <p className="text-caption text-ink-low">
            {formatTokens(seats.total)} members · sorted by total tokens
          </p>
        </div>
        <label className="inline-flex h-10 min-w-72 items-center gap-2 rounded-lg border border-line bg-raised px-3 text-body text-ink-low">
          <Search aria-hidden="true" className="h-4 w-4" />
          <input
            value={props.search}
            onChange={(event) => props.onSearch(event.target.value)}
            placeholder="Search name, email, org unit..."
            className="min-w-0 flex-1 bg-transparent text-ink placeholder:text-ink-faint focus:outline-none"
            data-testid="usage-seat-search"
          />
        </label>
      </header>
      {seats.rows.length === 0 ? (
        <p className="p-5 text-body text-ink-low" data-testid="usage-by-seat-empty">
          No member usage matches this window.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[84rem] border-collapse text-body" data-testid="usage-by-seat-table">
            <thead>
              <tr className="border-b border-line bg-raised text-left font-mono text-mono-label uppercase text-ink-low">
                <th className="px-4 py-3 font-medium">Member</th>
                <th className="px-3 py-3 font-medium">Org unit</th>
                <th className="px-3 py-3 font-medium">Primary model</th>
                <th className="px-3 py-3 font-medium">Tokens in</th>
                <th className="px-3 py-3 font-medium">Tokens out</th>
                <th className="px-3 py-3 font-medium">Cache write</th>
                <th className="px-3 py-3 font-medium">Cache read</th>
                <th className="px-3 py-3 font-medium">Requests</th>
                <th className="px-3 py-3 font-medium">Avg / req</th>
                <th className="px-3 py-3 font-medium">Cost</th>
                <th className="px-3 py-3 font-medium">Monthly cap</th>
                <th className="px-3 py-3 font-medium">14d trend</th>
              </tr>
            </thead>
            <tbody>
              {seats.rows.map((seat) => (
                <tr
                  key={seat.memberAccountId}
                  className="border-b border-line transition-colors hover:bg-raised"
                  data-testid={`usage-seat-row-${seat.memberAccountId}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar label={displaySeatName(seat)} />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-ink">{displaySeatName(seat)}</p>
                        <p className="truncate font-mono text-caption text-ink-low">{seat.email ?? seat.memberAccountId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="rounded-md bg-raised px-2 py-1 text-caption font-medium text-ink-mid">
                      {seat.department ?? 'Unassigned'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="rounded-md bg-raised px-2 py-1 font-mono text-caption text-ink">
                      {seat.primaryModel ?? seat.defaultModel ?? '--'}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-mono text-ink">{formatCompactCount(seat.inputTokens)}</td>
                  <td className="px-3 py-3 font-mono text-ink">{formatCompactCount(seat.outputTokens)}</td>
                  <td className="px-3 py-3 font-mono text-ink">{formatCompactCount(seat.cacheWriteTokens)}</td>
                  <td className="px-3 py-3 font-mono text-ink">{formatCompactCount(seat.cacheReadTokens)}</td>
                  <td className="px-3 py-3 font-mono text-ink">{formatTokens(seat.requests)}</td>
                  <td className="px-3 py-3 font-mono text-ink">{seat.averageTokensPerRequest === null ? '--' : formatCompactCount(seat.averageTokensPerRequest)}</td>
                  <td className="px-3 py-3 font-mono text-forest">
                    {formatUsdMicrosOrCentsAsCnyMetered(seat.costUsdMicros, seat.costUsdCents, props.cnyPerUsd)}
                  </td>
                  <td className="px-3 py-3 font-mono text-ink-mid">
                    {seat.monthlyLimitUsdCents === null ? '--' : formatUsdCentsAsCny(seat.monthlyLimitUsdCents, props.cnyPerUsd)}
                  </td>
                  <td className="px-3 py-3">
                    <Sparkline values={seat.trend14d} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <footer className="flex items-center justify-between gap-3 p-4 text-body text-ink-low">
        <span>
          Showing {seats.total === 0 ? 0 : seats.offset + 1}-{Math.min(seats.offset + seats.limit, seats.total)} of{' '}
          {seats.total}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={seats.offset === 0}
            onClick={() => props.onOffset(Math.max(0, seats.offset - seats.limit))}
            className="rounded-md border border-line px-2 py-1 disabled:opacity-40"
            data-testid="usage-seat-prev"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={seats.offset + seats.limit >= seats.total}
            onClick={() => props.onOffset(seats.offset + seats.limit)}
            className="rounded-md border border-line px-2 py-1 disabled:opacity-40"
            data-testid="usage-seat-next"
          >
            Next
          </button>
        </div>
      </footer>
    </article>
  );
}

/**
 * Display label for a by-key row's SKU (epic #898 P6 #913). Rows are one
 * per key×SKU; the stored `skuName` is the CANONICAL `namespace/model`,
 * so it is brand-aliased at render time through the catalog (`sku_id` →
 * brand-aliased `sku_name`). Legacy rows (null `skuId`) fall back to
 * their historical channel name.
 */
function byKeyRowModelLabel(
  entry: UsageByKeyEntry,
  brandedSkuNameById: ReadonlyMap<string, string>,
): string | null {
  if (typeof entry.skuId === 'string' && entry.skuId.length > 0) {
    const branded = brandedSkuNameById.get(entry.skuId);
    if (branded !== undefined) return branded;
  }
  if (typeof entry.skuName === 'string' && entry.skuName.length > 0) return entry.skuName;
  if (typeof entry.channelName === 'string' && entry.channelName.length > 0) {
    return entry.channelName;
  }
  if (typeof entry.channelSlug === 'string' && entry.channelSlug.length > 0) {
    return entry.channelSlug;
  }
  return null;
}

/** Stable per-row identity now that a key can appear once per SKU. */
function byKeyRowId(entry: UsageByKeyEntry): string {
  return `${entry.apiKeyId}:${entry.skuId ?? entry.channelId ?? 'legacy'}`;
}

function MemberKeyPanel(props: {
  readonly keys: readonly UsageByKeyEntry[];
  readonly isLoading: boolean;
  readonly cnyPerUsd: number | null;
  readonly brandedSkuNameById: ReadonlyMap<string, string>;
}): JSX.Element {
  return (
    <article className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5">
      <PanelHeader title="My API keys" subtitle="Usage generated by your own keys." />
      {props.isLoading ? (
        <Skeleton className="h-44" />
      ) : props.keys.length === 0 ? (
        <p className="text-body text-ink-low">No key usage in this window.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {props.keys.slice(0, 6).map((key) => (
            <div
              key={byKeyRowId(key)}
              className="grid grid-cols-1 gap-3 border-b border-line pb-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto]"
              data-testid={`usage-member-key-${testIdSlug(byKeyRowId(key))}`}
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-ink">{key.apiKeyName ?? key.apiKeyPrefix ?? 'Unnamed key'}</p>
                <p
                  className="truncate font-mono text-caption text-ink-low"
                  data-testid={`usage-member-key-model-${testIdSlug(byKeyRowId(key))}`}
                >
                  {byKeyRowModelLabel(key, props.brandedSkuNameById) ?? 'Unassigned channel'}
                </p>
                <p className="font-mono text-caption text-ink-low">{formatTokens(key.successRequests)} ok · {formatTokens(key.cacheHitRequests)} cache hits</p>
              </div>
              <div className="grid min-w-56 grid-cols-4 gap-2 text-right">
                <KeyCacheMetric label="In" value={formatCompactCount(key.inputTokens)} />
                <KeyCacheMetric label="Out" value={formatCompactCount(key.outputTokens)} />
                <KeyCacheMetric label="Write" value={formatCompactCount(key.cacheWriteTokens)} />
                <KeyCacheMetric label="Read" value={formatCompactCount(key.cacheReadTokens)} />
                <p className="col-span-4 font-mono text-caption text-ink-low">
                  Req {formatNullablePercent(key.requestCacheHitRate)} · Token {formatNullablePercent(key.tokenCacheHitRate)} · R/W {formatRatio(key.cacheReadWriteRatio)}
                </p>
                <p className="col-span-4 font-mono text-body text-forest">
                  {formatUsdMicrosOrCentsAsCnyMetered(key.costUsdMicros, key.costUsdCents, props.cnyPerUsd)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function KeyCacheMetric({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="font-mono text-mono-label uppercase text-ink-low">{label}</span>
      <span className="font-mono text-caption font-medium text-ink">{value}</span>
    </span>
  );
}

function MemberRequestPanel(props: {
  readonly rows: readonly UsageRequestLogEntry[];
  readonly isLoading: boolean;
}): JSX.Element {
  return (
    <article className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5">
      <PanelHeader title="Recent requests" subtitle="Billing metadata only; prompts and responses are not collected." />
      {props.isLoading ? (
        <Skeleton className="h-44" />
      ) : props.rows.length === 0 ? (
        <p className="text-body text-ink-low">No request metadata in this window.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {props.rows.slice(0, 8).map((row) => (
            <div
              key={row.id}
              className="grid grid-cols-1 gap-3 border-b border-line pb-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto]"
              data-testid={`usage-request-row-${testIdSlug(row.requestId)}`}
            >
              <div className="min-w-0">
                <p className="truncate font-mono text-body text-ink">{row.model}</p>
                <p className="font-mono text-caption text-ink-low">{row.requestId}</p>
              </div>
              {/* #1670: unit-metered rows dash instead of showing fake 0s. */}
              <div className="grid min-w-56 grid-cols-4 gap-2 text-right">
                <KeyCacheMetric label="In" value={unitAwareTokenCell(row, formatCompactCount(row.inputTokens))} />
                <KeyCacheMetric label="Out" value={unitAwareTokenCell(row, formatCompactCount(row.outputTokens))} />
                <KeyCacheMetric label="Write" value={unitAwareTokenCell(row, formatCompactCount(row.cacheWriteTokens))} />
                <KeyCacheMetric label="Read" value={unitAwareTokenCell(row, formatCompactCount(row.cacheReadTokens))} />
                <p className="col-span-4 font-mono text-caption text-ink-low">Status {row.statusCode}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function Avatar({ label }: { readonly label: string }): JSX.Element {
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'M';
  return (
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-mono text-caption font-medium text-on-forest">
      {initials}
    </span>
  );
}

function Sparkline({ values }: { readonly values: readonly number[] }): JSX.Element {
  const max = Math.max(1, ...values);
  return (
    <div className="flex h-8 items-end gap-0.5" data-testid="usage-seat-sparkline">
      {values.map((value, index) => (
        <span
          key={index}
          className="w-1.5 rounded-sm bg-forest/40"
          style={{ height: `${Math.max(2, (value / max) * 28)}px` }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function AdminDashboardSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4" data-testid="usage-admin-loading">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3, 4, 5].map((item) => (
          <Skeleton key={item} className="h-32" />
        ))}
      </div>
      <Skeleton className="h-80" />
      <Skeleton className="h-96" />
    </div>
  );
}

function UsageError({ error, testId }: { readonly error: unknown; readonly testId: string }): JSX.Element {
  return (
    <p className="rounded-lg border border-bad-line bg-card p-4 text-body text-bad" role="alert" data-testid={testId}>
      {error instanceof PlatformApiError ? `${error.code}: ${error.message}` : 'Could not load usage data.'}
    </p>
  );
}

function rangeFromDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function triggerCsvDownload(csv: string, filename: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (typeof URL.createObjectURL !== 'function' || typeof URL.revokeObjectURL !== 'function') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function topTrendGroups(
  points: readonly AdminUsageTrendPoint[],
  metric: TokenMetric,
  limit: number,
): string[] {
  const totals = new Map<string, number>();
  for (const point of points) {
    totals.set(point.groupKey, (totals.get(point.groupKey) ?? 0) + trendValue(point, metric));
  }
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([group]) => group);
}

function trendValue(point: AdminUsageTrendPoint, metric: TokenMetric): number {
  if (metric === 'in') return point.inputTokens;
  if (metric === 'out') return point.outputTokens;
  return point.inputTokens + point.outputTokens;
}

/**
 * The 6 series slots shared by the stacked trend chart (`fill-*`) and
 * the mix-panel ramps/swatches (`bg-*`). The two arrays MUST stay
 * slot-for-slot mirrors of each other; they are spelled out as full
 * literals (not `prefix-${token}` templates) because Tailwind's content
 * scanner only picks up complete class strings.
 */
const TREND_FILL_CLASSES = [
  'fill-forest',
  'fill-forest-bright',
  'fill-rust',
  'fill-rust-bright',
  'fill-line-strong',
  'fill-ink-faint',
] as const;

const LEGEND_BG_CLASSES = [
  'bg-forest',
  'bg-forest-bright',
  'bg-rust',
  'bg-rust-bright',
  'bg-line-strong',
  'bg-ink-faint',
] as const;

function trendColorClass(index: number): string {
  return TREND_FILL_CLASSES[index % TREND_FILL_CLASSES.length] ?? 'fill-forest';
}

function legendColorClass(index: number): string {
  return LEGEND_BG_CLASSES[index % LEGEND_BG_CLASSES.length] ?? 'bg-forest';
}

function displaySeatName(seat: Pick<AdminUsageSeatRow, 'memberName' | 'email' | 'memberAccountId'>): string {
  return seat.memberName ?? seat.email ?? seat.memberAccountId;
}

function summarizeMemberCache(keys: readonly UsageByKeyEntry[]): MemberCacheSummary {
  const totals = keys.reduce(
    (acc, key) => ({
      successRequests: acc.successRequests + key.successRequests,
      cacheHitRequests: acc.cacheHitRequests + key.cacheHitRequests,
      cacheEligibleInputTokens: acc.cacheEligibleInputTokens + key.cacheEligibleInputTokens,
      cacheReadTokens: acc.cacheReadTokens + key.cacheReadTokens,
      cacheWriteTokens: acc.cacheWriteTokens + key.cacheWriteTokens,
    }),
    {
      successRequests: 0,
      cacheHitRequests: 0,
      cacheEligibleInputTokens: 0,
      cacheReadTokens: 0,
      cacheWriteTokens: 0,
    },
  );
  return {
    ...totals,
    requestCacheHitRate: nullableRatio(totals.cacheHitRequests, totals.successRequests),
    tokenCacheHitRate: nullableRatio(totals.cacheReadTokens, totals.cacheEligibleInputTokens),
    cacheReadWriteRatio: nullableRatio(totals.cacheReadTokens, totals.cacheWriteTokens),
  };
}

function formatCompactCount(count: number): string {
  if (!Number.isFinite(count) || count < 0) return '--';
  if (count < 10000) return formatTokens(count);
  return COMPACT_FORMATTER.format(Math.floor(count));
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '--';
  return PERCENT_FORMATTER.format(value);
}

function formatNullablePercent(value: number | null): string {
  return value === null ? '--' : formatPercent(value);
}

function formatRatio(value: number | null): string {
  return value === null || !Number.isFinite(value) ? '--' : `${value.toFixed(1)}x`;
}

function nullableRatio(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

function formatLatency(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--';
  return `${formatTokens(value)} ms`;
}

function formatIsoDate(iso: string): string {
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) return iso.length > 0 ? iso : '--';
  const d = new Date(ms);
  return [d.getFullYear(), pad2(d.getMonth() + 1), pad2(d.getDate())].join('-');
}

function formatTime(iso: string): string {
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) return '--';
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function testIdSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
